/**
 * CardImageLightbox — полноразмерный просмотр обложки карточки (владелец
 * 2026-08-16: «глазик» на витринных плитках НЕ-инструментных разделов —
 * фотостудия, ленты трендов; в гриде «Инструментов» демо «до/после» и так
 * раскрывает суть, глазик там не нужен).
 *
 * Видео-карточка: вместо кадра открывается сам ролик (poster — обложка).
 * Мультистиль: несколько картинок-стилей — снизу пилюли (мини-превью + имя
 * стиля), клик переключает просмотр; ряд скроллится (тач-скролл в мобиле).
 * Обычная карточка: одна картинка, пилюль нет.
 *
 * Закрытие: клик по фону, крестик, Esc. Скролл страницы на время заперт.
 */
import { useEffect, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

import { setSoundPref, soundPref } from '../utils/cardSound'
import MediaPlayer from './MediaPlayer/MediaPlayer'

export interface LightboxImage {
	/** Картинка — она же постер, если это видео. */
	url: string
	label?: string
	/** Аудио-дорожка: смотреть нечего, открываем плеер с волной (референсы, 19.09). */
	audio?: string
	/** Видео-карточка: смотреть надо ролик, а не его первый кадр (владелец
	 *  2026-08-17). Пилюли мультистиля при этом остаются картинками. */
	video?: string
}

export default function CardImageLightbox({ images, initial = 0, badge, action, onClose }: {
	images: LightboxImage[]
	initial?: number
	/** Плашка в углу: «что это такое» — например «Референс · видео» (19.09), чтобы
	 *  открытый файл не путали с результатом генерации. */
	badge?: string
	/** Действие под картинкой — например «Открыть аватар» у витрины ассетов (13.10): на телефоне наведения нет, и
	 *  кнопка на самой плитке не видна. Функция — действие от ОТКРЫТОЙ картинки (чат 22.10: «Скачать» ту, что
	 *  выбрана пилюлей из нескольких). */
	action?: ReactNode | ((current: LightboxImage) => ReactNode)
	onClose: () => void
}) {
	const [idx, setIdx] = useState(() => Math.max(0, Math.min(initial, images.length - 1)))

	useEffect(() => {
		const onKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape') onClose()
		}
		window.addEventListener('keydown', onKey)
		// Запираем скролл БЕЗ прыжка к верху (owner 2026-08-17): body уходит в
		// fixed на текущей позиции, при закрытии позиция восстанавливается.
		const scrollY = window.scrollY
		const prev = {
			position: document.body.style.position,
			top: document.body.style.top,
			width: document.body.style.width,
		}
		document.body.style.position = 'fixed'
		document.body.style.top = `-${scrollY}px`
		document.body.style.width = '100%'
		return () => {
			window.removeEventListener('keydown', onKey)
			document.body.style.position = prev.position
			document.body.style.top = prev.top
			document.body.style.width = prev.width
			window.scrollTo(0, scrollY)
		}
	}, [onClose])

	const cur = images[idx] ?? images[0]

	// Звук в «глазике» — по умолчанию ВКЛЮЧЁН (владелец 12.09): открытие — это
	// клик, после которого политика автоплея звук обычно разрешает. Плеер сам
	// пробует стартовать со звуком и откатывается к тишине, если браузер не
	// пустил. Просил тишину в этой сессии — стартуем без звука; переключил
	// руками — запоминаем на сессию (cardSound).
	// Зазвучав, плеер объявляет звук на страницу (MediaPlayer.sharedSound):
	// плитка под лайтбоксом и превью виджета глохнут — источник один.

	if (!cur) return null
	const actionNode = typeof action === 'function' ? action(cur) : action

	return createPortal(
		<div className={`card-lightbox${actionNode ? ' card-lightbox--action' : ''}`} onClick={onClose} role="dialog" aria-modal="true" aria-label="Просмотр изображения">
			<button type="button" className="card-lightbox__close" onClick={onClose} aria-label="Закрыть">
				×
			</button>
			{badge && <span className="card-lightbox__badge">{badge}</span>}
			{cur.audio ? (
				<div className="card-lightbox__audio" onClick={(e) => e.stopPropagation()}>
					<MediaPlayer
						variant="audio"
						src={cur.audio}
						autoPlay
						startMuted={false}
						className="card-lightbox__player"
					/>
				</div>
			) : cur.video ? (
				<div className="card-lightbox__video" onClick={(e) => e.stopPropagation()}>
					<MediaPlayer
						variant="video"
						src={cur.video}
						poster={cur.url || undefined}
						autoPlay
						loop
						startMuted={soundPref() === false}
						className="card-lightbox__player"
						videoClassName="card-lightbox__img"
						onUserMuteToggle={(m) => setSoundPref(!m)}
					/>
				</div>
			) : (
				<img
					className="card-lightbox__img"
					src={cur.url}
					alt={cur.label || ''}
					onClick={(e) => e.stopPropagation()}
					draggable={false}
				/>
			)}
			{actionNode && <div className="card-lightbox__action" onClick={(e) => e.stopPropagation()}>{actionNode}</div>}
			{images.length > 1 && (
				<div className="card-lightbox__pills" onClick={(e) => e.stopPropagation()}>
					{images.map((im, i) => (
						<button
							key={i}
							type="button"
							className={`card-lightbox__pill${i === idx ? ' is-on' : ''}`}
							onClick={() => setIdx(i)}
						>
							<img src={im.url} alt="" loading="lazy" decoding="async" draggable={false} />
							{im.label && <span>{im.label}</span>}
						</button>
					))}
				</div>
			)}
		</div>,
		document.body,
	)
}
